/**
 * Tenant Utilities
 * Path-based tenant detection and resolution
 */

import { supabase } from './supabase';

// Reserved paths that cannot be used as tenant IDs
const RESERVED_PATHS = [
  'login', 'signup', 'register', 'forgot-password', 'reset-password',
  'super-admin', 'admin', 'api', 'auth', 'health', 'static', 'assets',
  'images', 'css', 'js', 'fonts', 'favicon.ico', 'robots.txt', 'sitemap.xml'
];

// Reserved subdomains that cannot be used as tenant IDs
const RESERVED_SUBDOMAINS = [
  'admin', 'api', 'www', 'mail', 'smtp', 'ftp', 'localhost', 'app',
  'dashboard', 'portal', 'test', 'staging', 'dev', 'demo', 'cdn',
  'static', 'assets', 'images', 'docs', 'help', 'support', 'blog'
];

/**
 * Extract tenant ID from URL path
 */
export const getTenantFromPath = () => {
  const path = window.location.pathname;
  const pathParts = path.split('/').filter(Boolean);
  
  if (pathParts.length === 0) {
    return null;
  }
  
  const potentialTenantId = pathParts[0].toLowerCase();
  
  // Check if it's a reserved path
  if (RESERVED_PATHS.includes(potentialTenantId)) {
    return null;
  }
  
  // Validate tenant ID format (lowercase, alphanumeric, hyphens only)
  if (!/^[a-z0-9-]+$/.test(potentialTenantId)) {
    return null;
  }
  
  // Must be at least 3 characters
  if (potentialTenantId.length < 3) {
    return null;
  }
  
  // Cannot start or end with hyphen
  if (potentialTenantId.startsWith('-') || potentialTenantId.endsWith('-')) {
    return null;
  }
  
  return potentialTenantId;
};

/**
 * Legacy: Extract tenant from subdomain
 */
export const getTenantSubdomain = () => {
  const host = window.location.host;
  const parts = host.split('.');
  
  const platformDomains = ['netlify.app', 'vercel.app', 'pages.dev', 'github.io', 'onrender.com', 'localhost'];
  const isPlatformDomain = platformDomains.some(domain => host.includes(domain));
  
  if (isPlatformDomain) {
    return null;
  }
  
  if (parts.length >= 3 && parts[0] !== 'www' && parts[0] !== 'admin') {
    return parts[0];
  }
  return null;
};

/**
 * Validate if a string is a valid tenant ID format
 */
export const isValidTenantId = (tenantId) => {
  if (!tenantId || typeof tenantId !== 'string') {
    return false;
  }
  
  const id = tenantId.toLowerCase();
  
  if (id.length < 3 || id.length > 20) {
    return false;
  }
  
  if (!/^[a-z0-9-]+$/.test(id)) {
    return false;
  }
  
  if (id.startsWith('-') || id.endsWith('-')) {
    return false;
  }
  
  if (RESERVED_PATHS.includes(id)) {
    return false;
  }
  
  if (RESERVED_SUBDOMAINS.includes(id)) {
    return false;
  }
  
  return true;
};

/**
 * Validate tenant ID against database
 */
export const validateTenant = async (tenantId) => {
  if (!isValidTenantId(tenantId)) {
    return {
      valid: false,
      tenant: null,
      error: 'Invalid tenant ID format'
    };
  }
  
  try {
    const { data: tenant, error } = await supabase
      .from('tenants')
      .select('id, name, subdomain, status, logo_url, settings')
      .eq('subdomain', tenantId)
      .eq('status', 'active')
      .maybeSingle();
    
    if (error) {
      return {
        valid: false,
        tenant: null,
        error: 'Failed to validate tenant'
      };
    }
    
    if (!tenant) {
      return {
        valid: false,
        tenant: null,
        error: 'Tenant not found'
      };
    }
    
    return {
      valid: true,
      tenant,
      error: null
    };
  } catch (err) {
    return {
      valid: false,
      tenant: null,
      error: 'Failed to validate tenant'
    };
  }
};


